import React, { useEffect, useMemo, useState } from 'react';
import type { LabelConfig, IconType, IconCategory, CustomTemplate, VariantSettings } from '../types';
import { ICONS } from '../constants';
import { generateLabelSvg, calculateDynamicLayout, A4_WIDTH, A4_HEIGHT } from '../lib/svgGenerator';
import { svgAspectRatio } from '../lib/templateRatio';
import { useFontsVersion } from '../lib/fontsVersion';
import StepperInput from './StepperInput';
import AiIconGenerator from './AiIconGenerator';
import AiStickerDesigner from './AiStickerDesigner';
import { WIZARD_SHAPES, type WizardShape, type WizardShapeDefault } from './SetupWizard';

// Redesigned setup wizard (návrh 02): 1) name, 2) pick any number of motifs, 3) size and count.
// It hands the editor the finished sticker settings, built the same way as the original wizard did.

type WrapMode = '1' | '2' | '3';

interface Pick {
  key: string;
  kind: 'template' | 'shape';
  templateId?: string;
  shape?: WizardShape;
  icon?: IconType;
  /** Variant of the admin's slot this pick starts from (keeps the admin's gaps, scales, …). */
  slotVariantId?: string;
  w: number;
  count: number;
  /** Count the admin saved for this slot — kept as is when the sheet gets split. */
  adminCount?: number;
  /** Made with the AI generator in this wizard. */
  ai?: boolean;
}

interface Props {
  nameLabel: string;
  classNameLabel: string;
  defaultNameValue?: string;
  defaultClassNameValue?: string;
  title?: string;
  intro?: string;
  appLogo?: string; // kept for compatibility, the dialog no longer shows the logo
  icons: IconType[];
  categories: IconCategory[];
  allowAi: boolean;
  customTemplates: CustomTemplate[];
  allowedShapes?: WizardShape[];
  shapeDefaults?: Partial<Record<WizardShape, WizardShapeDefault>>;
  baseVariantSettings: Record<string, VariantSettings>;
  /** The sheet's shared icon, so previews match the editor. */
  baseIcon?: IconType;
  sheetWidth?: number;
  sheetHeight?: number;
  onIconsChange: (icons: IconType[]) => void;
  /** Celá samolepka s AI: přidá zákazníkovu šablonu (jen v jeho prohlížeči). */
  onAddLocalTemplate?: (t: CustomTemplate[]) => void;
  /** settings: the stickers to put on the sheet (every other sticker gets hidden); firstKey: the sticker to open in the editor. */
  onComplete: (name: string, className: string, settings: Record<string, VariantSettings>, firstKey: string | null) => void;
  onSkip: () => void;
}

const MARGIN = 7;
const GAP = 2;

const shapeLabel: Record<WizardShape, string> = { square: 'Čtverec s ikonou', circle: 'Kolečko s ikonou', rectangle: 'Obdélník s ikonou', template: 'Šablona' };

type Filter = 'all' | 'motif' | 'oval' | 'label' | 'shape';

const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many);

const SetupWizardRd: React.FC<Props> = ({
  nameLabel, classNameLabel, defaultNameValue, defaultClassNameValue, title, intro, appLogo, icons, categories, allowAi,
  customTemplates, allowedShapes, shapeDefaults, baseVariantSettings, baseIcon, sheetWidth = A4_WIDTH, sheetHeight = A4_HEIGHT,
  onIconsChange, onAddLocalTemplate, onComplete, onSkip,
}) => {
  const fontsVersion = useFontsVersion();
  const shapes = useMemo(() => {
    const allowed = WIZARD_SHAPES.filter(s => !allowedShapes || allowedShapes.includes(s.id));
    return allowed.length > 0 ? allowed : WIZARD_SHAPES;
  }, [allowedShapes]);
  const noIcon = ICONS.find(i => i.id === 'no-icon') ?? ICONS[0];

  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [className, setClassName] = useState('');
  const [wrap, setWrap] = useState<WrapMode>('1');
  const [filter, setFilter] = useState<Filter>('all');
  const [showAi, setShowAi] = useState(false);
  const [showAiSticker, setShowAiSticker] = useState(false);
  const [countsEdited, setCountsEdited] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !showAi) onSkip(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showAi, onSkip]);

  // Height / width of every template: SVG from its own size, PNG once the image has loaded.
  const [ratios, setRatios] = useState<Record<string, number>>({});
  useEffect(() => {
    let alive = true;
    for (const t of customTemplates) {
      if (ratios[t.id]) continue;
      const svgRatio = t.data?.startsWith('data:image/svg+xml') ? svgAspectRatio(t.data) : null;
      if (svgRatio) { setRatios(r => ({ ...r, [t.id]: svgRatio })); continue; }
      const img = new Image();
      img.onload = () => { if (alive && img.naturalWidth) setRatios(r => ({ ...r, [t.id]: img.naturalHeight / img.naturalWidth })); };
      img.src = t.data;
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTemplates]);

  const ratioOf = (p: Pick) => {
    if (p.kind === 'template') return ratios[p.templateId!] ?? 1;
    return p.shape === 'rectangle' ? 16 / 26 : 1;
  };
  const heightOf = (p: Pick) => Math.max(3, Math.round(p.w * ratioOf(p) * 10) / 10);

  // Starting selection = what the shop's admin set up for the wizard's slots, so "Pokračovat" alone gives today's sheet.
  const defaultPicks = (): Pick[] => shapes.map(s => {
    const d = shapeDefaults?.[s.id];
    const t = d?.template && customTemplates.some(c => c.id === d.template!.id) ? d.template : undefined;
    return t
      ? { key: `slot-${s.id}`, kind: 'template', templateId: t.id, slotVariantId: s.variantId, w: t.w, count: d?.count || 0, adminCount: d?.count || undefined }
      : { key: `slot-${s.id}`, kind: 'shape', shape: s.id, icon: d?.icon, slotVariantId: s.variantId, w: s.w, count: d?.count || 0, adminCount: d?.count || undefined };
  });
  const [picks, setPicksRaw] = useState<Pick[]>(defaultPicks);
  const [touched, setTouched] = useState(false);
  const setPicks: typeof setPicksRaw = v => { setTouched(true); setPicksRaw(v); };
  // Settings (templates, admin slots) may arrive after the wizard opened — refresh the starting selection until the customer changes it.
  useEffect(() => { if (!touched) setPicksRaw(defaultPicks()); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapes, shapeDefaults, customTemplates]);

  const isPicked = (k: string) => picks.some(p => p.key === k);
  const toggleTemplate = (t: CustomTemplate) => {
    const existing = picks.find(p => p.kind === 'template' && p.templateId === t.id);
    if (existing) { setPicks(ps => ps.filter(p => p !== existing)); return; }
    // Sensible starting width by proportions: picture motifs 26 mm, ovals 40 mm, notebook labels 60 mm.
    const r = ratios[t.id] ?? 1;
    const w = r >= 0.75 ? 26 : r >= 0.4 ? 40 : 60;
    setPicks(ps => [...ps, { key: `tpl-${t.id}-${Date.now()}`, kind: 'template', templateId: t.id, w, count: 0 }]);
  };
  const toggleShape = (s: typeof WIZARD_SHAPES[number]) => {
    const existing = picks.find(p => p.kind === 'shape' && p.shape === s.id && !p.ai);
    if (existing) { setPicks(ps => ps.filter(p => p !== existing)); return; }
    setPicks(ps => [...ps, { key: `shape-${s.id}-${Date.now()}`, kind: 'shape', shape: s.id, icon: shapeDefaults?.[s.id]?.icon, slotVariantId: s.variantId, w: s.w, count: 0 }]);
  };
  const removePick = (key: string) => setPicks(ps => ps.filter(p => p.key !== key));
  const updatePick = (key: string, changes: Partial<Pick>) => setPicks(ps => ps.map(p => (p.key === key ? { ...p, ...changes } : p)));

  // Settings of one picked sticker — used both for the previews and for the sheet handed to the editor.
  const settingsOf = (p: Pick): VariantSettings => {
    const base = p.slotVariantId ? baseVariantSettings[p.slotVariantId] : undefined;
    const cfg = p.shape ? WIZARD_SHAPES.find(s => s.id === p.shape) : undefined;
    const h = heightOf(p);
    const wrapFields = { wrapText: wrap !== '1', forceTextSplit: wrap === '3', ...(wrap === '2' ? { fontScale: 0.7 } : wrap === '3' ? { fontScale: 0.6 } : {}) };
    if (p.kind === 'template') {
      return {
        ...(base ?? { iconScale: 1.0, fontScale: 1.0, themeId: '' }),
        ...(base ? {} : { fontScale: 1.0 }),
        visible: true,
        themeId: '',
        shapeId: undefined,
        isFullImage: true,
        templateId: p.templateId,
        fullImageData: undefined,
        customWidth: p.w,
        customHeight: h,
        customCount: p.count,
        ...(p.icon ? { customIcon: p.icon } : {}),
        ...wrapFields,
      } as VariantSettings;
    }
    return {
      ...(base ?? { iconScale: 0.55, fontScale: h < 20 ? 0.7 : 1.0, themeId: '', isBold: true, enableShading: true, textAlign: 'center' }),
      visible: true,
      themeId: '',
      shapeId: cfg?.shapeId,
      isFullImage: false,
      templateId: undefined,
      fullImageData: undefined,
      customWidth: p.w,
      customHeight: h,
      customCount: p.count,
      ...(p.icon ? { customIcon: p.icon } : {}),
      ...wrapFields,
    } as VariantSettings;
  };

  const shownName = name.trim() || defaultNameValue || 'Vaše jméno';
  const previewConfig = (vs: Record<string, VariantSettings>): LabelConfig => ({
    name: shownName,
    className: className.trim(),
    icon: baseIcon ?? noIcon,
    themeId: 'blue',
    variantSettings: vs,
  });
  const svgOf = (p: Pick, idSuffix: string) => {
    const s = settingsOf(p);
    return generateLabelSvg(previewConfig({ p: s }), 'p', 0, 0, s.customWidth || 26, s.customHeight || 20, false, [], [], `wz-${idSuffix}`, false, customTemplates);
  };

  // Counts that share the sheet evenly between the picked motifs (like the original wizard did).
  const evenCounts = (list: Pick[]) => {
    const cw = sheetWidth - MARGIN * 2;
    const ch = sheetHeight - MARGIN * 2;
    const per = ch / Math.max(1, list.length);
    return list.map(p => {
      const h = heightOf(p);
      const cols = Math.max(1, Math.floor((cw + GAP) / (p.w + GAP)));
      const rows = Math.max(1, Math.floor((per + GAP) / (h + GAP)));
      return { ...p, count: p.adminCount || cols * rows, _cols: cols } as Pick & { _cols: number };
    });
  };
  // Even split, then keep adding whole rows (in turn) while everything still fits on one sheet.
  const fillSheet = (list: Pick[]): Pick[] => {
    let cur = evenCounts(list).map(({ _cols, ...p }: any) => ({ p: p as Pick, cols: _cols as number }));
    const fits = (arr: Pick[]) => {
      const vs: Record<string, VariantSettings> = {};
      arr.forEach((p, i) => { vs[`f${i}`] = settingsOf(p); });
      return calculateDynamicLayout(previewConfig(vs), sheetWidth, sheetHeight).length <= 1;
    };
    if (!fits(cur.map(c => c.p))) return cur.map(c => c.p);
    let grew = true;
    while (grew) {
      grew = false;
      for (let i = 0; i < cur.length; i++) {
        if (cur[i].p.adminCount) continue;
        const next = cur.map((c, j) => (j === i ? { ...c, p: { ...c.p, count: c.p.count + c.cols } } : c));
        if (fits(next.map(c => c.p))) { cur = next; grew = true; }
      }
    }
    return cur.map(c => c.p);
  };
  const goToStep3 = () => {
    setPicks(ps => { if (!countsEdited) return fillSheet(ps); const even = evenCounts(ps); return ps.map((p, i) => (p.count > 0 ? p : { ...p, count: even[i].count })); });
    setStep(3);
  };

  const finalSettings = useMemo(() => {
    const out: Record<string, VariantSettings> = {};
    const usedSlots = new Set<string>();
    picks.forEach((p, i) => {
      // The first pick of a slot keeps the slot's own variant id; any further one gets its own.
      let key: string;
      if (p.slotVariantId && !usedSlots.has(p.slotVariantId)) { key = p.slotVariantId; usedSlots.add(p.slotVariantId); }
      else key = `${p.kind === 'template' ? 'template' : p.slotVariantId || 'shape'}-${Date.now().toString(36)}-${i}`;
      out[key] = settingsOf(p);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picks, wrap, ratios, baseVariantSettings]);

  const pages = useMemo(() => calculateDynamicLayout(previewConfig(finalSettings), sheetWidth, sheetHeight),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [finalSettings, sheetWidth, sheetHeight, name, className, fontsVersion]);
  const totalStickers = pages.reduce((n, p) => n + p.length, 0);

  const finish = () => {
    const keys = Object.keys(finalSettings);
    onComplete(name.trim(), className.trim(), finalSettings, keys[0] ?? null);
  };

  // Filters by proportions (templates have no categories in the data).
  const groupOf = (t: CustomTemplate): Exclude<Filter, 'all' | 'shape'> => {
    const r = ratios[t.id] ?? 1;
    return r >= 0.75 ? 'motif' : r >= 0.4 ? 'oval' : 'label';
  };
  const counts = useMemo(() => {
    const c = { motif: 0, oval: 0, label: 0 };
    customTemplates.forEach(t => { c[groupOf(t)]++; });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTemplates, ratios]);
  const filters: { id: Filter; label: string; n: number }[] = [
    { id: 'all', label: 'Vše', n: customTemplates.length + shapes.length },
    { id: 'motif', label: 'Obrázkové motivy', n: counts.motif },
    { id: 'oval', label: 'Ovály', n: counts.oval },
    { id: 'label', label: 'Štítky na sešity', n: counts.label },
    { id: 'shape', label: 'Tvary s ikonou', n: shapes.length },
  ].filter(f => f.id === 'all' || f.n > 0) as { id: Filter; label: string; n: number }[];

  const visibleTemplates = customTemplates.filter(t => filter === 'all' || filter === groupOf(t));
  const showShapes = filter === 'all' || filter === 'shape';

  const pickTitle = (p: Pick) => p.kind === 'template'
    ? (customTemplates.find(t => t.id === p.templateId)?.name || 'Šablona')
    : (p.ai ? 'Vlastní obrázek (AI)' : shapeLabel[p.shape!]);

  const firstName = (name.trim() || '').split(/\s+/)[0];
  const steps = ['Jméno', 'Motivy', 'Velikost a počet'];
  const canNext = step === 1 ? name.trim().length > 0 : step === 2 ? picks.length > 0 : totalStickers > 0;

  return (
    <div className="rdw-overlay">
    <div className="rdw" role="dialog" aria-modal="true" aria-label="Rychlé nastavení samolepek">
      <header className="rdw-header">
        <span className="rdw-title">Rychlé nastavení</span>
        <ol className="rdw-steps" aria-label="Kroky průvodce">
          {steps.map((label, i) => {
            const n = i + 1;
            const state = n < step ? 'done' : n === step ? 'on' : '';
            return (
              <li key={label} className={`rdw-step ${state}`} aria-current={n === step ? 'step' : undefined}>
                <span className="rdw-step-dot">{n < step ? '✓' : n}</span>
                <span className="rdw-step-label">{label}</span>
              </li>
            );
          })}
        </ol>
        <button type="button" className="rdw-close" onClick={onSkip} aria-label="Zavřít rychlé nastavení" title="Zavřít (Esc)">×</button>
      </header>

      <div className="rdw-body">
        {step === 1 && (
          <div className="rdw-name">
            <div className="rdw-name-card">
              <h1 className="rdw-h1">{title || 'Čí samolepky to budou?'}</h1>
              <p className="rdw-lead">{intro || 'Napište jméno, které se objeví na každé samolepce. Motivy vyberete v dalším kroku.'}</p>
              <label className="rdw-label" htmlFor="rdw-name">{nameLabel}</label>
              <input id="rdw-name" className="rdw-input" autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={defaultNameValue || 'Petr Novák'} onKeyDown={e => e.key === 'Enter' && canNext && setStep(2)} />
              <label className="rdw-label" htmlFor="rdw-class">{classNameLabel} <span className="rdw-opt">(nepovinné)</span></label>
              <input id="rdw-class" className="rdw-input" value={className} onChange={e => setClassName(e.target.value)} placeholder={defaultClassNameValue || '3.B'} onKeyDown={e => e.key === 'Enter' && canNext && setStep(2)} />
              <span className="rdw-label">Jméno na samolepce</span>
              <div className="rdw-seg" role="radiogroup" aria-label="Zalomení textu">
                {(['1', '2', '3'] as WrapMode[]).map(m => (
                  <button key={m} type="button" role="radio" aria-checked={wrap === m} className={wrap === m ? 'on' : ''} onClick={() => setWrap(m)}>{m === '1' ? 'Na 1 řádek' : m === '2' ? 'Na 2 řádky' : 'Na 3 řádky'}</button>
                ))}
              </div>
            </div>
            <div className="rdw-name-preview" aria-hidden="true">
              <p className="rdw-label">Takhle bude jméno vypadat</p>
              <div className="rdw-name-stickers">
                {picks.slice(0, 3).map((p, i) => (
                  <svg key={p.key} viewBox={`-1 -1 ${p.w + 2} ${heightOf(p) + 2}`} style={{ width: `${Math.round(p.w * 5.2)}px` }} className="rdw-sticker" dangerouslySetInnerHTML={{ __html: svgOf(p, `n${i}`) }} />
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="rdw-pick">
            <section className="rdw-pick-main">
              <h1 className="rdw-h1">{firstName ? `Vyberte motivy pro ${firstName}` : 'Vyberte motivy'}</h1>
              <p className="rdw-lead">Můžete jich zvolit víc, na jednom archu se zkombinují. Jméno už je doplněné.</p>
              <div className="rdw-chips rd-chips" role="tablist" aria-label="Filtr motivů">
                {filters.map(f => (
                  <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} className={`rdw-chip ${filter === f.id ? 'on' : ''}`} onClick={() => setFilter(f.id)}>{f.label} <span>{f.n}</span></button>
                ))}
                {allowAi && (
                  <button type="button" className="rdw-chip rdw-chip-ai" onClick={() => setShowAi(true)}>✦ Vlastní obrázek s AI</button>
                )}
                {allowAi && onAddLocalTemplate && (
                  <button type="button" className="rdw-chip rdw-chip-ai" onClick={() => setShowAiSticker(true)}>✦ Celá samolepka s AI</button>
                )}
              </div>
              <div className="rdw-grid">
                {visibleTemplates.map(t => {
                  const on = picks.some(p => p.kind === 'template' && p.templateId === t.id);
                  const tmp: Pick = { key: t.id, kind: 'template', templateId: t.id, w: 26, count: 0 };
                  return (
                    <button key={t.id} type="button" className={`rdw-tile ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => toggleTemplate(t)}>
                      {on && <span className="rdw-check" aria-hidden="true">✓</span>}
                      <span className="rdw-tile-img"><svg viewBox={`-1 -1 28 ${heightOf(tmp) + 2}`} dangerouslySetInnerHTML={{ __html: svgOf(tmp, `t-${t.id}`) }} /></span>
                      <span className="rdw-tile-name">{t.name || 'Šablona'}</span>
                    </button>
                  );
                })}
                {showShapes && shapes.map(s => {
                  const on = picks.some(p => p.kind === 'shape' && p.shape === s.id && !p.ai);
                  const tmp: Pick = { key: s.id, kind: 'shape', shape: s.id, icon: shapeDefaults?.[s.id]?.icon, slotVariantId: s.variantId, w: s.w, count: 0 };
                  return (
                    <button key={s.id} type="button" className={`rdw-tile ${on ? 'on' : ''}`} aria-pressed={on} onClick={() => toggleShape(s)}>
                      {on && <span className="rdw-check" aria-hidden="true">✓</span>}
                      <span className="rdw-tile-img"><svg viewBox={`-1 -1 ${s.w + 2} ${heightOf(tmp) + 2}`} dangerouslySetInnerHTML={{ __html: svgOf(tmp, `s-${s.id}`) }} /></span>
                      <span className="rdw-tile-name">{shapeLabel[s.id]}</span>
                    </button>
                  );
                })}
              </div>
            </section>
            <aside className="rdw-pick-side">
              <div className="rdw-side-head"><h2 className="rdw-h2">Váš výběr</h2><span>{picks.length} {plural(picks.length, 'motiv', 'motivy', 'motivů')}</span></div>
              <div className="rdw-side-preview">
                {picks.length === 0 ? <p className="rdw-lead">Zatím nic. Klikněte na motivy vlevo.</p> : picks.map((p, i) => (
                  <svg key={p.key} viewBox={`-1 -1 ${p.w + 2} ${heightOf(p) + 2}`} style={{ width: `${Math.round(p.w * 3.4)}px` }} className="rdw-sticker" dangerouslySetInnerHTML={{ __html: svgOf(p, `v${i}`) }} />
                ))}
              </div>
              <ul className="rdw-side-list">
                {picks.map(p => (
                  <li key={p.key}><strong>{pickTitle(p)}</strong><button type="button" onClick={() => removePick(p.key)}>odebrat</button></li>
                ))}
              </ul>
              <p className="rdw-note">V dalším kroku nastavíte velikost a počet kusů. Každý motiv můžete potom upravit i jednotlivě.</p>
            </aside>
          </div>
        )}

        {step === 3 && (
          <div className="rdw-pick">
            <section className="rdw-pick-main">
              <h1 className="rdw-h1">Velikost a počet</h1>
              <p className="rdw-lead">Počty jsou předvyplněné tak, aby se motivy na arch rozdělily rovnoměrně. Můžete je změnit.</p>
              <ul className="rdw-size-list">
                {picks.map((p, i) => (
                  <li key={p.key} className="rdw-size-row">
                    <span className="rdw-size-thumb"><svg viewBox={`-1 -1 ${p.w + 2} ${heightOf(p) + 2}`} dangerouslySetInnerHTML={{ __html: svgOf(p, `z${i}`) }} /></span>
                    <span className="rdw-size-name"><strong>{pickTitle(p)}</strong><span>{p.w} × {heightOf(p)} mm</span></span>
                    <label className="rdw-size-field"><span>Šířka (mm)</span><StepperInput value={p.w} min={10} max={120} onChange={v => updatePick(p.key, { w: Math.max(10, v) })} /></label>
                    <label className="rdw-size-field"><span>Počet kusů</span><StepperInput value={p.count} min={0} onChange={v => { setCountsEdited(true); updatePick(p.key, { count: Math.max(0, v) }); }} /></label>
                    <button type="button" className="rdw-remove" onClick={() => removePick(p.key)} aria-label={`Odebrat ${pickTitle(p)}`}>odebrat</button>
                  </li>
                ))}
              </ul>
              <button type="button" className="rdw-btn-ghost" onClick={() => { setCountsEdited(false); setPicks(ps => fillSheet(ps)); }}>Rozdělit arch rovnoměrně</button>
            </section>
            <aside className="rdw-pick-side">
              <div className="rdw-side-head"><h2 className="rdw-h2">Tiskový arch</h2><span>{totalStickers} {plural(totalStickers, 'samolepka', 'samolepky', 'samolepek')} · {pages.length} {plural(pages.length, 'arch', 'archy', 'archů')}</span></div>
              <div className="rdw-sheet">
                <svg viewBox={`0 0 ${sheetWidth} ${sheetHeight}`} className="rdw-sheet-svg">
                  {(pages[0] || []).map((it, idx) => (
                    <g key={idx} dangerouslySetInnerHTML={{ __html: generateLabelSvg(previewConfig(finalSettings), it.variant, it.x, it.y, it.w, it.h, false, [], [], undefined, false, customTemplates) }} />
                  ))}
                </svg>
              </div>
              {pages.length > 1 && <p className="rdw-note">Samolepky se nevejdou na jeden arch – sada bude mít {pages.length} archy. Pro jeden arch snižte počty nebo velikost.</p>}
            </aside>
          </div>
        )}
      </div>

      <footer className="rdw-footer">
        <button type="button" className="rdw-btn-ghost" onClick={() => (step > 1 ? setStep(step - 1) : onSkip())}>
          {step === 1 ? 'Upravit ručně' : step === 2 ? '← Zpět ke jménu' : '← Zpět k motivům'}
        </button>
        <div className="rdw-footer-right">
          {step === 2 && picks.length > 0 && <span className="rdw-footer-sum">Vybráno: {picks.map(pickTitle).join(', ')}</span>}
          {step === 3 && <span className="rdw-footer-sum">{totalStickers} {plural(totalStickers, 'samolepka', 'samolepky', 'samolepek')} na sadu</span>}
          <button type="button" className="rdw-btn-primary" disabled={!canNext} onClick={() => (step === 1 ? setStep(2) : step === 2 ? goToStep3() : finish())}>
            {step === 3 ? 'Vytvořit samolepky →' : 'Pokračovat →'}
          </button>
        </div>
      </footer>

      {showAiSticker && onAddLocalTemplate && (
        <AiStickerDesigner
          name={shownName}
          className={className.trim()}
          onClose={() => setShowAiSticker(false)}
          onDone={aiPicks => {
            onAddLocalTemplate(aiPicks.map(p => p.template));
            setRatios(rs => ({ ...rs, ...Object.fromEntries(aiPicks.map(p => [p.template.id, p.ratio])) }));
            const stamp = Date.now();
            setPicks(ps => [...ps, ...aiPicks.flatMap((p, i) => p.sizes.map((sz, j) => ({ key: `tpl-${p.template.id}-${stamp}-${i}-${j}`, kind: 'template' as const, templateId: p.template.id, w: sz.w, count: 0 })))]);
            setShowAiSticker(false);
          }}
        />
      )}
      {showAi && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4">
          <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <AiIconGenerator
              categories={categories}
              isAdmin={false}
              onIconGenerated={(base64, prompt, catId) => {
                const newIcon: IconType = { id: `ai-${Date.now()}`, name: prompt, data: base64, dataType: 'url', isCustom: true, categoryId: catId };
                onIconsChange([...icons, newIcon]);
                const circle = WIZARD_SHAPES.find(s => s.id === 'circle')!;
                setPicks(ps => [...ps, { key: `ai-${Date.now()}`, kind: 'shape', shape: 'circle', icon: newIcon, slotVariantId: circle.variantId, w: circle.w, count: 0, ai: true }]);
                setShowAi(false);
              }}
              onCancel={() => setShowAi(false)}
            />
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default SetupWizardRd;
